import type { ITransaction } from "@/data/model/Order";
import type { IMultiLanguageText } from "@/data/model/Shared";
import { role } from "@/data/util/Config";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";

import { Component } from "@/shared/elements/Component";
import { translate } from "@/shared/localization/language";

import { i18n, isInRole } from "@/portal/Trans";
import { CancellationStateBadge } from "@/portal/elements/CancellationStateBadge";
import { PaymentStateBadge } from "@/portal/elements/transaction/PaymentStateBadge";

export class PaymentInfoHeader extends Component {
    public readonly transaction: ITransaction;

    constructor(order: ITransaction) {
        super();
        this.transaction = order;
        this.$element = this.drawElement();
    }

    protected drawElement() {
        // draw order header fields
        const $element = $('<div class="payment-info-header" />');

        // 1. set summary row
        const $summaryRow = $('<div class="summary-row" />').appendTo($element);

        const $paymentInfo = $('<div class="payment-info" />').appendTo($summaryRow);
        $paymentInfo.append(`<div class="table">${this.getTableName()}</div>`);

        // Set waiter or end user name
        if (this.transaction.userId == null) {
            $paymentInfo.append(`<div class="waiter">${i18n("general.deletedUser")}</div>`);
        } else {
            if (this.transaction.userName === "Anonymer User") {
                if (isInRole(role.Admin)) {
                    $paymentInfo.append(`<div class="waiter"><a href="user/${this.transaction.userId}">${i18n("user.anonymousUser")}</a></div>`);
                } else {
                    $paymentInfo.append(`<div class="waiter">${i18n("user.anonymousUser")}</div>`);
                }
            } else if (this.transaction.userName != null && this.transaction.userName !== "") {
                if (isInRole(role.Admin)) {
                    $paymentInfo.append(`<div class="waiter"><a href="user/${this.transaction.userId}">${this.transaction.userName}</a></div>`);
                } else {
                    $paymentInfo.append(`<div class="waiter">${this.transaction.userName}</div>`);
                }
            }
        }

        // set external payment id
        $paymentInfo.append(`<div class="external-payment-id">${this.transaction.externalPaymentId}</div>`);

        // set order time info
        const $timeInfo = $('<div class="payment-time-info" />').appendTo($summaryRow);
        $timeInfo.append(
            `<div class="time">${this.getPaymentTime()}</div>` + `<div class="date">${this.getPaymentDate()}</div>`
        );

        // 2. set statues row
        const $statusesRow = $('<dic class="statuses-row" />').appendTo($element);
        $("<div></div>").appendTo($statusesRow);

        // set payment states info
        const $paymentState = $(`<div class="payment-states" />`).appendTo($statusesRow);
        if (this.transaction.payUseCaseName != null) {
            const $useCase = $('<div class="use-case" />').appendTo($paymentState);
            $useCase.append(`<span>${this.getUseCaseName(this.transaction.payUseCaseName)}</span>`);
        }

        PaymentStateBadge.renderTo($paymentState, this.transaction.state);

        if (this.transaction.cancellationState != null) {
            CancellationStateBadge.renderTo($paymentState, this.transaction.cancellationState);
        }

        return $element;
    }

    private getTableName(): string | undefined {
        if (this.transaction.tableId == null) return i18n("order.deletedTable");
        return Format.htmlEscape(this.transaction.tableName);
    }

    private getPaymentTime(): string {
        return DateTime.toTimeString(this.transaction.confirmDate ?? this.transaction.created, true);
    }

    private getPaymentDate(): string {
        return DateTime.toDateString(this.transaction.confirmDate ?? this.transaction.created);
    }

    private getUseCaseName(name: IMultiLanguageText): string {
        return Format.htmlEscape(translate(name))!;
    }

    public static render($appendToTarget: JQuery, order: ITransaction): PaymentInfoHeader {
        const component = new PaymentInfoHeader(order);
        component.renderTo($appendToTarget);

        return component;
    }
}
